// Challenge API utilities
import { supabase } from '../supabase';

export async function getChallengesActive() {
  const { data, error } = await supabase.from('challenges').select('*, user_challenge_progress(*)').eq('is_active', true).lte('start_date', new Date().toISOString()).gte('end_date', new Date().toISOString());
  if (error) throw error;
  return { success: true, challenges: data || [] };
}

export async function getChallengesCompleted(limit = 20) {
  const { data, error } = await supabase.from('user_challenge_progress').select('*, challenges(*)').eq('completed', true).limit(limit);
  if (error) throw error;
  return { success: true, challenges: data || [] };
}

export async function getChallengesUpcoming(limit = 10) {
  const { data, error } = await supabase.from('challenges').select('*').gt('start_date', new Date().toISOString()).limit(limit);
  if (error) throw error;
  return { success: true, challenges: data || [] };
}

export async function filterChallenges(filters = {}) {
  const { data: { user } } = await supabase.auth.getUser();
  let query = supabase.from('challenges').select('*').eq('is_active', true);
  if (filters.sport && filters.sport !== 'all') query = query.eq('sport_type', filters.sport);
  if (filters.difficulty && filters.difficulty !== 'all') query = query.eq('difficulty', filters.difficulty);
  if (filters.duration && filters.duration !== 'all') query = query.eq('duration', filters.duration);
  const { data, error } = await query.order('end_date');
  if (error) throw error;
  const { data: progress, error: progressError } = user
    ? await supabase.from('user_challenge_progress').select('*').eq('user_id', user.id)
    : { data: [], error: null };
  if (progressError) throw progressError;
  const progressByChallenge = new Map((progress || []).map(item => [item.challenge_id, {
    ...item, _id: item.id, userId: item.user_id, userStatus: item.user_status,
    currentValue: item.current_value, rewardClaimed: item.reward_claimed
  }]));
  return { success: true, challenges: (data || []).map(challenge => ({
    ...challenge, _id: challenge.id, sportType: challenge.sport_type,
    targetValue: challenge.target_value, metricType: challenge.metric_type,
    rewardXP: challenge.reward_xp, startDate: challenge.start_date,
    endDate: challenge.end_date, userProgress: progressByChallenge.get(challenge.id) || null
  })) };
}

export async function getChallengeStats() {
  const { data, error } = await supabase.from('user_challenge_progress').select('completed, reward_claimed');
  if (error) throw error;
  const progress = data || [];
  return { success: true, stats: { total: progress.length, completed: progress.filter(item => item.completed).length, rewardsClaimed: progress.filter(item => item.reward_claimed).length } };
}

export async function updateChallengeProgress(metricType, value, sportType) {
  const { data: { user } } = await supabase.auth.getUser();
  const { data: progress, error } = await supabase.from('user_challenge_progress').select('*, challenges(*)').eq('user_id', user.id).eq('user_status', 'active');
  if (error) throw error;
  for (const item of (progress || []).filter(item => item.challenges?.metric_type === metricType && (!sportType || item.challenges.sport_type === sportType))) {
    const currentValue = Number(item.current_value) + Number(value);
    const percent = Math.min(100, currentValue / Number(item.challenges.target_value) * 100);
    const { error: updateError } = await supabase.from('user_challenge_progress').update({ current_value: currentValue, progress: percent, completed: percent >= 100, user_status: percent >= 100 ? 'completed' : 'active', completed_at: percent >= 100 ? new Date().toISOString() : null }).eq('id', item.id);
    if (updateError) throw updateError;
  }
  return { success: true };
}

export async function claimChallengeReward(progressId) {
  const { data: progress, error: progressError } = await supabase.from('user_challenge_progress').select('*, challenges(reward_xp)').eq('id', progressId).single();
  if (progressError) throw progressError;
  if (!progress.completed || progress.reward_claimed) throw new Error('Reward unavailable');
  const { data: { user } } = await supabase.auth.getUser();
  const { data: profile, error: profileError } = await supabase.from('profiles').select('total_xp').eq('id', user.id).single();
  if (profileError) throw profileError;
  const { error: updateProfileError } = await supabase.from('profiles').update({ total_xp: profile.total_xp + progress.challenges.reward_xp }).eq('id', user.id);
  if (updateProfileError) throw updateProfileError;
  const { data, error } = await supabase.from('user_challenge_progress').update({ reward_claimed: true, claimed_at: new Date().toISOString() }).eq('id', progressId).select().single();
  if (error) throw error;
  return { success: true, progress: data };
}

export async function getChallengeRewardInfo(challengeId) {
  const { data, error } = await supabase.from('challenges').select('id, title, reward_xp, reward_points, reward_badge').eq('id', challengeId).single();
  if (error) throw error;
  return { success: true, rewardInfo: { challengeId: data.id, title: data.title, rewardXP: data.reward_xp, rewardPoints: data.reward_points, rewardBadge: data.reward_badge } };
}

export async function participateChallenge(challengeId) {
  const { data: { user } } = await supabase.auth.getUser();
  const { data: existing, error: existingError } = await supabase.from('user_challenge_progress').select('user_status').eq('user_id', user.id).eq('challenge_id', challengeId).maybeSingle();
  if (existingError) throw existingError;
  if (existing?.user_status === 'cancelled') throw new Error('Participation définitivement annulée pour ce challenge');
  const { data, error } = await supabase.from('user_challenge_progress').upsert({ user_id: user.id, challenge_id: challengeId, user_status: 'active' }, { onConflict: 'user_id,challenge_id' }).select().single();
  if (error) throw error;
  return { success: true, progress: { ...data, _id: data.id, userId: data.user_id, userStatus: data.user_status, currentValue: data.current_value } };
}

export async function submitProof(progressId, proofData) {
  const { data, error } = await supabase.from('user_challenge_progress').update({ proof_url: proofData.proofUrl || null, proof_description: proofData.proofDescription || '', proof_status: 'pending', proof_submitted_at: new Date().toISOString(), user_status: 'pending_review' }).eq('id', progressId).select().single();
  if (error) throw error;
  return { success: true, progress: { ...data, _id: data.id, userId: data.user_id, userStatus: data.user_status, currentValue: data.current_value } };
}

export async function approveProof(progressId) {
  const { data, error } = await supabase.from('user_challenge_progress').update({ proof_status: 'approved', user_status: 'completed', completed: true, proof_approved_at: new Date().toISOString() }).eq('id', progressId).select().single();
  if (error) throw error;
  return { success: true, progress: data };
}

export async function rejectProof(progressId, reason) {
  const { data, error } = await supabase.from('user_challenge_progress').update({ proof_status: 'rejected', user_status: 'active', rejection_reason: reason || '' }).eq('id', progressId).select().single();
  if (error) throw error;
  return { success: true, progress: data };
}

export async function cancelChallenge(progressId) {
  const { data, error } = await supabase.from('user_challenge_progress').update({ user_status: 'cancelled' }).eq('id', progressId).select().single();
  if (error) throw error;
  return { success: true, progress: data };
}

// Admin management helpers
export async function createChallenge(data) {
  const { data: challenge, error } = await supabase.from('challenges').insert({
    title: data.title,
    description: data.description,
    sport_type: data.sportType || null,
    difficulty: data.difficulty,
    target_value: data.targetValue,
    metric_type: data.metricType,
    reward_xp: data.rewardXP,
    reward_points: data.rewardPoints || 0,
    reward_badge: data.rewardBadge || null,
    duration: data.duration,
    start_date: data.startDate,
    end_date: data.endDate,
    icon: data.icon || 'target'
  }).select('*').single();
  if (error) throw error;
  return { success: true, challenge: { ...challenge, _id: challenge.id, sportType: challenge.sport_type } };
}
export async function updateChallenge(id, data) {
  const { data: challenge, error } = await supabase.from('challenges').update({
    title: data.title,
    description: data.description,
    sport_type: data.sportType || null,
    difficulty: data.difficulty,
    target_value: data.targetValue,
    metric_type: data.metricType,
    reward_xp: data.rewardXP,
    reward_points: data.rewardPoints || 0,
    reward_badge: data.rewardBadge || null,
    duration: data.duration,
    start_date: data.startDate,
    end_date: data.endDate,
    icon: data.icon || 'target'
  }).eq('id', id).select('*').single();
  if (error) throw error;
  return { success: true, challenge: { ...challenge, _id: challenge.id, sportType: challenge.sport_type } };
}
export async function deleteChallenge(id) {
  const { error } = await supabase.from('challenges').delete().eq('id', id);
  if (error) throw error;
}
export async function getAllChallengesAdmin() {
  const { data, error } = await supabase.from('challenges').select('*').order('start_date');
  if (error) throw error;
  return { success: true, challenges: (data || []).map(challenge => ({ ...challenge, _id: challenge.id, sportType: challenge.sport_type })) };
}

export async function getChallengeParticipants(challengeId) {
  const { data, error } = await supabase.from('user_challenge_progress').select('*, profiles(*)').eq('challenge_id', challengeId).in('user_status', ['active', 'pending_review', 'completed']);
  if (error) throw error;
  return { success: true, participants: data || [] };
}
